/**
 * The SVG arithmetic every chart on the editor shares.
 *
 * Kept free of React so it can be tested as the arithmetic it is: a tick ladder
 * that reads well, a bar that is rounded at its data end and square at the
 * baseline, and the band between two crossing lines. The charts themselves then
 * contain only layout and interaction.
 *
 * Every chart plots money on a single axis. There is deliberately no second
 * y-scale anywhere in this app — two arbitrary scales on one plot invent a
 * correlation that is not in the data — so yearly flows, the cumulative
 * position and the breakdown are three charts rather than one with overlays.
 * See docs/09-theme.md#charts.
 */

/* The drawing surface is a fixed user-space box scaled by the viewBox, so a
 * chart's proportions never depend on the width it lands at. */
export const CHART_W = 900;
export const PLOT_H = 190;
export const AXIS_H = 26;
export const PAD_L = 64;
export const PAD_R = 40;
export const PAD_T = 10;
export const SVG_H = PAD_T + PLOT_H + AXIS_H;
export const PLOT_W = CHART_W - PAD_L - PAD_R;

/** Columns stop growing past this, so a two-year scenario is not two slabs. */
export const BAR_MAX = 18;
export const BAR_GAP = 2;
/** Stacked columns carry segment labels, so they are allowed to be wider. */
export const STACK_MAX = 34;

/** Round an axis maximum up to a clean 1/2/2.5/5 × 10^n so ticks read well. */
export function niceMax(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const scaled = value / magnitude;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 2.5 ? 2.5 : scaled <= 5 ? 5 : 10;
  return step * magnitude;
}

/**
 * A tick ladder from zero to at least `max`. The count is a target rather than a
 * promise: a clean step that overshoots by one tick beats a ragged step that
 * lands exactly.
 */
export function axisTicks(max: number, targetCount = 5): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];
  const step = niceMax(max / targetCount);
  const count = Math.max(1, Math.ceil(max / step));
  return Array.from({ length: count + 1 }, (_, i) => step * i);
}

export interface SignedAxis {
  ticks: number[];
  /** The value at the top of the plot. */
  top: number;
  /** The value at the bottom — zero unless the data goes negative. */
  bottom: number;
}

/**
 * A tick ladder that can go below zero, for a series that does.
 *
 * The cumulative-net lines on the comparison screen start negative and — with
 * luck — end positive, and an axis pinned at zero would clip exactly the half
 * the reader came for. Zero is always on the ladder, because "when does this
 * cross zero" is the question the chart answers.
 */
export function signedAxisTicks(min: number, max: number, targetCount = 5): SignedAxis {
  const low = Math.min(0, min);
  const high = Math.max(0, max);
  if (high === low) return { ticks: [0, 1], top: 1, bottom: 0 };

  const step = niceMax((high - low) / targetCount);
  const bottom = Math.floor(low / step) * step;
  const top = Math.ceil(high / step) * step;
  const count = Math.round((top - bottom) / step);

  return {
    // Rounded back to the step: repeated addition of 2.5e6 drifts far enough to
    // print "7,500,000.0000001" on an axis label.
    ticks: Array.from(
      { length: count + 1 },
      (_, i) => Math.round((bottom + step * i) / step) * step,
    ),
    top,
    bottom,
  };
}

/** The top of the axis — what every value is drawn as a fraction of. */
export function axisTop(ticks: readonly number[]): number {
  return ticks[ticks.length - 1] ?? 1;
}

/** Rounded at the data end, square at the baseline. */
export function barPath(x: number, y: number, w: number, h: number, radius = 4): string {
  const r = Math.min(radius, w / 2, Math.max(0, h));
  const f = (n: number) => n.toFixed(1);
  return [
    `M${f(x)},${f(y + h)}`,
    `L${f(x)},${f(y + r)}`,
    `Q${f(x)},${f(y)} ${f(x + r)},${f(y)}`,
    `L${f(x + w - r)},${f(y)}`,
    `Q${f(x + w)},${f(y)} ${f(x + w)},${f(y + r)}`,
    `L${f(x + w)},${f(y + h)}`,
    "Z",
  ].join(" ");
}

/**
 * Park a line's end label clear of the other line without letting it escape the
 * plot: `above` sends it up, otherwise down.
 */
export function endLabelY(y: number, above: boolean): number {
  const offset = above ? -9 : 14;
  return Math.min(PAD_T + PLOT_H - 2, Math.max(PAD_T + 9, y + offset));
}

export interface BandPoint {
  x: number;
  yRevenue: number;
  yCost: number;
  /** Revenue minus cost in money, which is what decides the band's hue. */
  diff: number;
}

/**
 * The area between two cumulative lines, split at every crossing so the loss
 * stretch and the profit stretch can be washed in different hues.
 *
 * A crossing is interpolated rather than snapped to a year boundary — the point
 * of the chart is where the lines meet, and rounding that to the nearest year
 * would move payback by up to twelve months.
 */
export function bandRegions(points: readonly BandPoint[]): { positive: boolean; d: string }[] {
  const pathFor = (run: readonly BandPoint[]): string => {
    if (run.length < 2) return "";
    const forward = run
      .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.yRevenue.toFixed(1)}`)
      .join(" ");
    const backward = [...run]
      .reverse()
      .map((p) => `L${p.x.toFixed(1)},${p.yCost.toFixed(1)}`)
      .join(" ");
    return `${forward} ${backward} Z`;
  };

  const regions: { positive: boolean; d: string }[] = [];
  let run: BandPoint[] = [];
  let sign = 0;

  for (const point of points) {
    const pointSign = Math.sign(point.diff);
    if (run.length === 0) {
      run.push(point);
      sign = pointSign;
      continue;
    }
    if (pointSign === 0 || sign === 0 || pointSign === sign) {
      run.push(point);
      if (sign === 0) sign = pointSign;
      continue;
    }
    const previous = run[run.length - 1];
    if (!previous) continue;
    const t = previous.diff / (previous.diff - point.diff);
    const crossingY = previous.yRevenue + (point.yRevenue - previous.yRevenue) * t;
    const crossing: BandPoint = {
      x: previous.x + (point.x - previous.x) * t,
      yRevenue: crossingY,
      yCost: crossingY,
      diff: 0,
    };
    run.push(crossing);
    regions.push({ positive: sign > 0, d: pathFor(run) });
    run = [crossing, point];
    sign = pointSign;
  }
  if (run.length > 1) regions.push({ positive: sign >= 0, d: pathFor(run) });
  return regions;
}

/** Keep a tooltip's centre inside the card rather than half off its edge. */
export function tooltipLeftPct(x: number): number {
  return Math.min(86, Math.max(14, (x / CHART_W) * 100));
}
